import { useCallback, useEffect, useState } from "react";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Checkbox, SelectInput, TextInput, TextareaInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { Dialog } from "@/components/ui/Dialog";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import {
  createTagesgeschaeftMailbox,
  deleteTagesgeschaeftMailbox,
  getTagesgeschaeftMapping,
  listTagesgeschaeftMailboxes,
  patchTagesgeschaeftMailbox,
  putTagesgeschaeftMapping,
  scanTagesgeschaeftMailbox,
  suggestTagesgeschaeftMapping,
  type TagesgeschaeftFieldMappingItem,
  type TagesgeschaeftMailbox,
} from "@/lib/tagesgeschaeft";
import { listOrdner, listOrdnerJahre, type OrdnerOut } from "../../lib/settingsApi";

export function TagesgeschaeftMailboxesSection() {
  const [items, setItems] = useState<TagesgeschaeftMailbox[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [years, setYears] = useState<number[]>([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [ordner, setOrdner] = useState<OrdnerOut[]>([]);

  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [mailboxUpn, setMailboxUpn] = useState("");
  const [label, setLabel] = useState("");
  const [ordnerId, setOrdnerId] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [mappingOpen, setMappingOpen] = useState(false);
  const [mappingMailbox, setMappingMailbox] = useState<TagesgeschaeftMailbox | null>(null);
  const [mappingItems, setMappingItems] = useState<TagesgeschaeftFieldMappingItem[]>([]);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [mappingSaving, setMappingSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [scanningId, setScanningId] = useState<number | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await listTagesgeschaeftMailboxes());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Laden fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    void listOrdnerJahre()
      .then((ys) => {
        const nextYears = ys.length ? ys : [new Date().getFullYear()];
        setYears(nextYears);
        if (nextYears.length && !nextYears.includes(year)) {
          setYear(nextYears[0] ?? new Date().getFullYear());
        }
      })
      .catch(() => undefined);
  }, [year]);

  useEffect(() => {
    void listOrdner(year)
      .then(setOrdner)
      .catch(() => setOrdner([]));
  }, [year]);

  const openCreate = () => {
    setEditId(null);
    setMailboxUpn("");
    setLabel("");
    setOrdnerId("");
    setEnabled(true);
    setFormError(null);
    setFormOpen(true);
  };

  const openEdit = (row: TagesgeschaeftMailbox) => {
    setEditId(row.id);
    setMailboxUpn(row.mailbox_upn);
    setLabel(row.label || "");
    setOrdnerId(String(row.ordner_id));
    setEnabled(row.enabled);
    if (row.ordner_jahr) setYear(row.ordner_jahr);
    setFormError(null);
    setFormOpen(true);
  };

  const saveMailbox = async () => {
    setSaving(true);
    setFormError(null);
    try {
      const oid = Number(ordnerId);
      if (!mailboxUpn.trim() || !oid) {
        setFormError("Postfach und Ordner sind Pflicht.");
        return;
      }
      if (editId) {
        await patchTagesgeschaeftMailbox(editId, {
          mailbox_upn: mailboxUpn.trim(),
          ordner_id: oid,
          label: label.trim() || null,
          enabled,
        });
        toast.success("Postfach aktualisiert.");
      } else {
        await createTagesgeschaeftMailbox({
          mailbox_upn: mailboxUpn.trim(),
          ordner_id: oid,
          label: label.trim() || null,
          enabled,
        });
        toast.success("Postfach angelegt.");
      }
      setFormOpen(false);
      await reload();
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const removeMailbox = async (row: TagesgeschaeftMailbox) => {
    if (!window.confirm(`Postfach ${row.mailbox_upn} wirklich löschen?`)) return;
    try {
      await deleteTagesgeschaeftMailbox(row.id);
      toast.success("Postfach gelöscht.");
      await reload();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Löschen fehlgeschlagen.");
    }
  };

  const openMapping = async (row: TagesgeschaeftMailbox) => {
    setMappingMailbox(row);
    setMappingOpen(true);
    setMappingLoading(true);
    try {
      const res = await getTagesgeschaeftMapping(row.id);
      setMappingItems(res.items || []);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Mapping laden fehlgeschlagen.");
      setMappingOpen(false);
    } finally {
      setMappingLoading(false);
    }
  };

  const saveMapping = async () => {
    if (!mappingMailbox) return;
    setMappingSaving(true);
    try {
      await putTagesgeschaeftMapping(mappingMailbox.id, mappingItems);
      toast.success("Feld-Mapping gespeichert.");
      setMappingOpen(false);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Speichern fehlgeschlagen.");
    } finally {
      setMappingSaving(false);
    }
  };

  const runSuggest = async () => {
    if (!mappingMailbox) return;
    setSuggesting(true);
    try {
      const res = await suggestTagesgeschaeftMapping(mappingMailbox.id);
      setMappingItems((prev) => {
        const byKey = new Map(prev.map((p) => [p.field_key, p]));
        return (res.suggestions || []).map((s, idx) => {
          const existing = byKey.get(s.field_key);
          return {
            ...existing,
            field_key: s.field_key,
            label: s.label || existing?.label || s.field_key,
            enabled: s.enabled,
            extraction_hint: s.extraction_hint || "",
            example_value: s.example_value || "",
            sort_order: existing?.sort_order ?? (idx + 1) * 10,
          };
        });
      });
      toast.success(
        res.ai_enabled
          ? `${res.sample_count} Mails analysiert – Vorschläge übernommen.`
          : `Heuristik aus ${res.sample_count} Mails (OpenAI nicht konfiguriert).`,
      );
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Analyse fehlgeschlagen.");
    } finally {
      setSuggesting(false);
    }
  };

  const runScan = async (row: TagesgeschaeftMailbox) => {
    setScanningId(row.id);
    try {
      const res = await scanTagesgeschaeftMailbox(row.id, { days: 14, max_items: 30 });
      toast.success(`${res.scanned ?? 0} Mails gescannt.`);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Scan fehlgeschlagen.");
    } finally {
      setScanningId(null);
    }
  };

  return (
    <Card className="space-y-4 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-label">Tagesgeschäft-Postfächer</h2>
          <p className="mt-1 text-sm text-label-secondary">
            Pro Kunde ein Postfach, zugeordnet zu einem Ordner. Feld-Mappings steuern die
            KI-Analyse – ohne kundenspezifischen Code.
          </p>
        </div>
        <Button type="button" onClick={openCreate}>
          Postfach hinzufügen
        </Button>
      </div>

      {error ? (
        <Alert variant="error" title="Fehler">
          {error}
        </Alert>
      ) : null}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-label-secondary">
          <Spinner /> Laden …
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-label-secondary">Noch keine Postfächer konfiguriert.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="text-xs uppercase text-label-tertiary">
              <tr>
                <th className="px-2 py-2">Postfach</th>
                <th className="px-2 py-2">Ordner</th>
                <th className="px-2 py-2">Status</th>
                <th className="px-2 py-2 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} className="border-t border-separator">
                  <td className="px-2 py-3">
                    <div className="font-medium text-label">{row.label || row.mailbox_upn}</div>
                    {row.label ? (
                      <div className="text-xs text-label-secondary">{row.mailbox_upn}</div>
                    ) : null}
                  </td>
                  <td className="px-2 py-3">
                    <div className="text-label">
                      {row.ordner_nummer}
                      {row.ordner_jahr ? ` (${row.ordner_jahr})` : ""}
                    </div>
                    <div className="text-xs text-label-secondary">
                      {[row.kunden_name, row.ordner_beschreibung].filter(Boolean).join(" · ") || "—"}
                    </div>
                  </td>
                  <td className="px-2 py-3">
                    <span
                      className={
                        row.enabled
                          ? "rounded bg-status-green/15 px-2 py-0.5 text-xs text-status-green"
                          : "rounded bg-label-secondary/15 px-2 py-0.5 text-xs text-label-secondary"
                      }
                    >
                      {row.enabled ? "Aktiv" : "Aus"}
                    </span>
                  </td>
                  <td className="px-2 py-3">
                    <div className="flex flex-wrap justify-end gap-2">
                      <Button type="button" size="sm" variant="secondary" onClick={() => void openMapping(row)}>
                        Mapping
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled={scanningId === row.id}
                        onClick={() => void runScan(row)}
                      >
                        {scanningId === row.id ? "Scan …" : "Scan"}
                      </Button>
                      <Button type="button" size="sm" variant="secondary" onClick={() => openEdit(row)}>
                        Bearbeiten
                      </Button>
                      <Button type="button" size="sm" variant="danger" onClick={() => void removeMailbox(row)}>
                        Löschen
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editId ? "Postfach bearbeiten" : "Postfach hinzufügen"}
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setFormOpen(false)}>
              Abbrechen
            </Button>
            <Button type="button" disabled={saving} onClick={() => void saveMailbox()}>
              {saving ? "Speichern …" : "Speichern"}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          {formError ? (
            <Alert variant="error" title="Fehler">
              {formError}
            </Alert>
          ) : null}
          <TextInput
            label="Postfach (UPN / E-Mail)"
            value={mailboxUpn}
            onChange={(e) => setMailboxUpn(e.target.value)}
            placeholder="kunde@ernsthaus.de"
          />
          <TextInput
            label="Bezeichnung (optional)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Kunde XY"
          />
          <SelectInput
            label="Jahr"
            value={String(year)}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </SelectInput>
          <SelectInput label="Ordner" value={ordnerId} onChange={(e) => setOrdnerId(e.target.value)}>
            <option value="">— Ordner wählen —</option>
            {ordner.map((o) => (
              <option key={o.id} value={o.id}>
                {o.nummer} – {o.kunden_name || ""} {o.beschreibung || ""}
              </option>
            ))}
          </SelectInput>
          <Checkbox
            label="Überwachung aktiv"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
        </div>
      </Dialog>

      <Dialog
        open={mappingOpen}
        onClose={() => setMappingOpen(false)}
        title={`Feld-Mapping${mappingMailbox ? `: ${mappingMailbox.mailbox_upn}` : ""}`}
        size="lg"
        footer={
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              disabled={suggesting || mappingLoading}
              onClick={() => void runSuggest()}
            >
              {suggesting ? "Analysiere …" : "Beispiel-Mails analysieren"}
            </Button>
            <Button type="button" disabled={mappingSaving || mappingLoading} onClick={() => void saveMapping()}>
              {mappingSaving ? "Speichern …" : "Mapping speichern"}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-label-secondary">
            Definieren Sie, welche Felder die Analyse aus den Mails dieses Postfachs ziehen soll.
            „Beispiel-Mails analysieren“ schlägt Hints aus den letzten Eingängen vor.
          </p>
          {mappingLoading ? (
            <div className="flex items-center gap-2 text-sm text-label-secondary">
              <Spinner /> Mapping wird geladen …
            </div>
          ) : (
            <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
              {mappingItems.map((item, idx) => (
                <div key={item.field_key} className="rounded-ios border border-separator p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <div className="font-medium text-label">{item.label || item.field_key}</div>
                    <Checkbox
                      label="Aktiv"
                      checked={item.enabled}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setMappingItems((prev) =>
                          prev.map((p, i) => (i === idx ? { ...p, enabled: checked } : p)),
                        );
                      }}
                    />
                  </div>
                  <div className="grid gap-2 md:grid-cols-2">
                    <TextareaInput
                      label="Extraktions-Hinweis für die KI"
                      value={item.extraction_hint || ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setMappingItems((prev) =>
                          prev.map((p, i) => (i === idx ? { ...p, extraction_hint: v } : p)),
                        );
                      }}
                      rows={2}
                    />
                    <TextInput
                      label="Beispielwert"
                      value={item.example_value || ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setMappingItems((prev) =>
                          prev.map((p, i) => (i === idx ? { ...p, example_value: v } : p)),
                        );
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Dialog>
    </Card>
  );
}
